import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Flags } from '@setvi/shared/enums';

import { PdvWarningIcon, PillButton } from 'componentsV2/pdv';
import { pdvProductQuery, usePdvIdentity } from 'Services/Query/Pdv';

import { FeedbackList } from './feedback-list';
import { ReportDialog } from './report-dialog';
import { useStyles } from './styles';

interface Props {
  sku?: string;
  /** overrides the signed-in user; only set it where there is no user context */
  by?: string;
  /** false where the reports are already listed elsewhere, e.g. a Feedback tab */
  showFeedbackList?: boolean;
}

/**
 * Lets a rep report a product data problem from the Catalog product details popup,
 * and see what happened to earlier reports on the same product. Behind the
 * rep-feedback flag, like the admin queue that reads these reports.
 */
export const ProductDataFeedback = ({
  sku,
  by,
  showFeedbackList = true
}: Props) => {
  const classes = useStyles();
  const flags = useFlags();
  const enabled = Boolean(flags[Flags.REP_FEEDBACK]);
  // With Product Data Verification on, the dialog has a Feedback tab instead.
  const listInTab = Boolean(flags[Flags.PRODUCT_DATA_VERIFICATION]);
  const identity = usePdvIdentity();
  const me = by || identity.id;
  const [view, setView] = useState<'report' | 'feedback' | null>(null);

  // Query eagerly so the button can hide before a rep ever clicks it — but not at
  // all for companies without the feature.
  const { data: raw } = useQuery({
    ...pdvProductQuery(sku ?? ''),
    enabled: enabled && Boolean(sku)
  });
  const notScored = raw && 'error' in raw ? raw.reason : null;
  const product = raw && !('error' in raw) ? raw : undefined;
  const openFlags = raw?.openFlags ?? [];
  const reports = raw?.flags ?? openFlags;

  // Inactive and discontinued products are not sellable, so there is nothing
  // useful a rep could report about them and nothing a PM would act on.
  if (
    !enabled ||
    !sku ||
    !raw ||
    notScored === 'inactive' ||
    notScored === 'discontinued'
  )
    return null;

  return (
    <div className={classes.triggers}>
      {showFeedbackList && !listInTab && reports.length > 0 && (
        <PillButton variant="text" onClick={() => setView('feedback')}>
          Feedback ({reports.length})
        </PillButton>
      )}
      <PillButton
        variant="text"
        startIcon={<PdvWarningIcon />}
        onClick={() => setView('report')}>
        Report a Data Issue
      </PillButton>

      {view === 'report' && (
        <ReportDialog
          sku={sku}
          desc={product?.desc}
          product={product}
          openFlags={openFlags}
          me={me}
          canSend={Boolean(me)}
          onClose={() => setView(null)}
        />
      )}

      {view === 'feedback' && (
        <FeedbackList
          sku={sku}
          desc={product?.desc}
          flags={reports}
          me={me}
          onClose={() => setView(null)}
        />
      )}
    </div>
  );
};
